import React, { Component } from "react";
import { Row, Col } from "antd";
import { withRouter } from "react-router-dom";
import { withAppContext } from "../store/contextStore";
import ValueTracker from "../common/ValueTracker";

const NAVY   = "#1A2280";
const GOLD   = "#D4921A";
const GREEN  = "#2D6A4F";
const MUTED  = "#718096";
const BORDER = "#E2E8F0";

// Stage 1: standalone public route for the Value Tracker, reachable directly
// from the header nav (not just buried in My Tools). Renders the exact same
// <ValueTracker /> component the "Value Tracker" tab in ViewProfile.js uses —
// nothing about that tab changes, this is just a second entry point to the
// same component.
//
// Stage 2: anonymous visitors land here and can browse the tool. Since they
// have no data yet, we show a marketing "hook" section above the tool
// explaining why they should set up a Value Tracker and run a valuation —
// pulled from the "10X More Accurate" brand messaging.
class ValueTrackerPage extends Component {
  renderHook() {
    const FEATURE_PILLS = [
      { icon: "📊", label: "Nationwide Data" },
      { icon: "🤖", label: "AI Photo Analysis" },
      { icon: "🏠", label: "Homeowner Input" },
    ];
    const SOURCES = [
      {
        icon: "📊",
        title: "Nationwide Data Engine",
        desc: "Millions of data points from across the U.S. — including sales, listings, foreclosures, trends & market shifts.",
      },
      {
        icon: "🤖",
        title: "AI Photo Analysis",
        desc: "Upload photos and our AI analyzes condition, upgrades, quality & features that actually move your value.",
      },
      {
        icon: "🏡",
        title: "Homeowner Import",
        desc: "You know your home best. Add renovations, upgrades & maintenance to personalize your estimate.",
      },
    ];
    const IMPROVEMENTS_EXAMPLE = [
      { label: "Kitchen Remodel", value: "+$25,000" },
      { label: "Bathroom Upgrade", value: "+$12,000" },
      { label: "New Roof", value: "+$8,500" },
      { label: "Finished Basement", value: "+$15,000" },
    ];
    const BOTTOM_STRIP = [
      { icon: "🎯", label: "More Accurate Value", desc: "10X better than automated estimates" },
      { icon: "💡", label: "Make Smarter Decisions", desc: "Buy, sell, invest or refinance with confidence" },
      { icon: "🛡️", label: "Protect & Grow Your Wealth", desc: "Track improvements and maximize your home's value" },
      { icon: "❤️", label: "Happier, Healthier Home", desc: "Tools to keep your home safe, efficient and enjoyable" },
    ];

    return (
      <div style={{ marginBottom: "18px" }}>
        {/* Hero */}
        <div
          style={{
            background: `linear-gradient(135deg, ${NAVY} 0%, #2A3390 100%)`,
            borderRadius: "16px",
            padding: "30px 26px",
            marginBottom: "16px",
            boxShadow: "0 6px 24px rgba(26,34,128,0.18)",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "40px", marginBottom: "6px" }}>🧘</div>
          <div style={{ color: GOLD, fontWeight: 700, fontSize: "12px", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: "6px" }}>
            My Home Buddha · Smarter Tools. Better Decisions.
          </div>
          <h1 style={{ color: "white", fontWeight: 900, fontSize: "26px", margin: "0 0 4px", lineHeight: 1.25 }}>
            A Better Home Value.<br />10X More Accurate.
          </h1>
          <div style={{ color: "rgba(255,255,255,0.75)", fontSize: "14px", marginBottom: "16px" }}>
            Powered by Data, AI &amp; You.
          </div>
          <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
            {FEATURE_PILLS.map(p => (
              <div key={p.label} style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "50px", padding: "6px 14px", color: "white", fontSize: "12px", fontWeight: 700 }}>
                {p.icon} {p.label}
              </div>
            ))}
          </div>
        </div>

        {/* 3 sources */}
        <div style={{ marginBottom: "8px", textAlign: "center" }}>
          <div style={{ fontSize: "15px", fontWeight: 800, color: NAVY }}>3 Powerful Sources. 1 Superior Value.</div>
        </div>
        <Row gutter={[12, 12]} style={{ marginBottom: "16px" }}>
          {SOURCES.map(s => (
            <Col key={s.title} xs={24} md={8}>
              <div style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "16px", height: "100%" }}>
                <div style={{ fontSize: "26px", marginBottom: "6px" }}>{s.icon}</div>
                <div style={{ fontWeight: 700, color: NAVY, fontSize: "13px", marginBottom: "4px" }}>{s.title}</div>
                <div style={{ fontSize: "12px", color: MUTED, lineHeight: 1.6 }}>{s.desc}</div>
              </div>
            </Col>
          ))}
        </Row>

        {/* Sample comparison */}
        <div style={{ background: "white", border: `1px solid ${BORDER}`, borderRadius: "12px", padding: "18px 20px", marginBottom: "16px" }}>
          <div style={{ fontSize: "11px", fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: ".04em", marginBottom: "10px", textAlign: "center" }}>
            Sample Home Example — Illustrative Only
          </div>
          <Row gutter={[10, 10]} align="middle">
            <Col xs={24} sm={8} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: MUTED, marginBottom: "2px" }}>MyHomeBuddha Value</div>
              <div style={{ fontSize: "22px", fontWeight: 900, color: GREEN }}>$725,000</div>
              <div style={{ fontSize: "11px", color: GREEN, fontWeight: 700 }}>92% Confidence Score</div>
            </Col>
            <Col xs={24} sm={16}>
              <Row gutter={[8, 8]}>
                {[["Zestimate", "$611,500"], ["Redfin Estimate", "$540,000"], ["Realtor.com", "$555,000"]].map(([label, val]) => (
                  <Col key={label} span={8} style={{ textAlign: "center" }}>
                    <div style={{ fontSize: "10px", color: MUTED }}>{label}</div>
                    <div style={{ fontSize: "14px", fontWeight: 700, color: "#A0AEC0" }}>{val}</div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
          <div style={{ textAlign: "center", fontSize: "11px", color: MUTED, marginTop: "10px", fontStyle: "italic" }}>
            More Data. More Insight. More Accurate.
          </div>
        </div>

        {/* Track value / build wealth */}
        <div style={{ background: `linear-gradient(135deg, ${GOLD}, #B8790F)`, borderRadius: "12px", padding: "18px 22px", marginBottom: "16px" }}>
          <div style={{ color: "#2A1600", fontWeight: 800, fontSize: "15px", marginBottom: "6px" }}>📈 Track Your Value. Build Your Wealth.</div>
          <div style={{ color: "#2A1600", fontSize: "12px", lineHeight: 1.6, marginBottom: "12px", opacity: 0.85 }}>
            Every improvement you make adds real, trackable value. Log renovations and watch your equity grow over time.
          </div>
          <Row gutter={[8, 8]}>
            {IMPROVEMENTS_EXAMPLE.map(imp => (
              <Col key={imp.label} xs={12} sm={6}>
                <div style={{ background: "rgba(255,255,255,0.5)", borderRadius: "8px", padding: "8px 10px", textAlign: "center" }}>
                  <div style={{ fontSize: "11px", color: "#2A1600", fontWeight: 600 }}>{imp.label}</div>
                  <div style={{ fontSize: "14px", color: GREEN, fontWeight: 800 }}>{imp.value}</div>
                </div>
              </Col>
            ))}
          </Row>
          <div style={{ textAlign: "center", fontSize: "11px", color: "#2A1600", marginTop: "10px", fontWeight: 600 }}>
            Your updates. Your records. Your value.
          </div>
        </div>

        {/* Bottom benefit strip */}
        <Row gutter={[10, 10]} style={{ marginBottom: "16px" }}>
          {BOTTOM_STRIP.map(b => (
            <Col key={b.label} xs={12} md={6}>
              <div style={{ textAlign: "center", padding: "10px 6px" }}>
                <div style={{ fontSize: "20px" }}>{b.icon}</div>
                <div style={{ fontSize: "11px", fontWeight: 700, color: NAVY, marginTop: "4px" }}>{b.label}</div>
                <div style={{ fontSize: "10px", color: MUTED, marginTop: "2px" }}>{b.desc}</div>
              </div>
            </Col>
          ))}
        </Row>

        <div style={{ textAlign: "center", marginBottom: "4px" }}>
          <div style={{ display: "inline-block", background: NAVY, color: "white", borderRadius: "50px", padding: "10px 24px", fontWeight: 800, fontSize: "13px" }}>
            👇 Get Your Free Home Value Below
          </div>
          <div style={{ fontSize: "11px", color: MUTED, marginTop: "10px", fontStyle: "italic" }}>
            Knowledge is Power. Your Home is Wealth.
          </div>
        </div>
      </div>
    );
  }

  render() {
    const userLogin = localStorage.getItem("userLogin");

    return (
      <div style={{ backgroundColor: "#F9FAFF", minHeight: "70vh" }}>
        <Row className="margin-body-agent">
          <Col span={24} style={{ marginTop: "1vw", marginBottom: "0.5vw" }}>
            <h2 style={{ color: NAVY, fontWeight: 800 }}>📊 Value Tracker</h2>
          </Col>
        </Row>
        {!userLogin && (
          <Row className="margin-body-agent">
            <Col span={24}>{this.renderHook()}</Col>
          </Row>
        )}
        <Row className="margin-body-agent">
          <Col
            span={24}
            style={{
              background: "white",
              borderRadius: 10,
              marginBottom: "2vw",
              overflow: "hidden",
            }}
          >
            <ValueTracker />
          </Col>
        </Row>
      </div>
    );
  }
}

export default withAppContext(withRouter(ValueTrackerPage));